import React from 'react';
import { PatientService, ServicesContext } from '~/services';
import { Dropdown, Spinner } from 'react-bootstrap';
import { reminderStatusOptions } from '~/util/reminderUtils';

interface ReminderStatusProps {
    reminder: ReminderType;
    setStatusDirty?: null | ((value: ((prevState: boolean) => boolean) | boolean) => void);
}

export function ReminderStatus({ reminder, setStatusDirty = null }: ReminderStatusProps) {
    const currentStatus = React.useRef(reminder.status);
    const servicesContext = React.useContext(ServicesContext);
    const [newStatus, setNewStatus] = React.useState(
        reminderStatusOptions.find((option) => option.value === reminder.status)
    );
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        if (newStatus!.value !== currentStatus.current) {
            setLoading(true);
            setTimeout(async () => {
                await servicesContext.patient.editReminder(
                    reminder,
                    { status: newStatus!.value },
                    !!setStatusDirty
                );
                if (reminder?.timely_action) {
                    // if currently open, and status set to any closed, deduct
                    if (
                        currentStatus.current === reminderStatusOptions[0].value &&
                        newStatus!.value !== reminderStatusOptions[0].value
                    ) {
                        servicesContext.events.publish(
                            PatientService.EVENTS.timely_action_counts_updated,
                            -1
                        );
                    }
                    // if currently not open, and status set to open, increase
                    if (
                        currentStatus.current !== reminderStatusOptions[0].value &&
                        newStatus!.value === reminderStatusOptions[0].value
                    ) {
                        servicesContext.events.publish(
                            PatientService.EVENTS.timely_action_counts_updated,
                            1
                        );
                    }
                }
                currentStatus.current = newStatus!.value;
                setLoading(false);
                if (setStatusDirty) setStatusDirty(true);
            }, 1000);
        }
    }, [newStatus, reminder, servicesContext.patient, servicesContext.events, setStatusDirty]);

    return (
        <Dropdown>
            <Dropdown.Toggle variant="secondary" id={`reminder-status-${reminder.id}`}>
                {loading && (
                    <Spinner
                        className="me-2"
                        animation="border"
                        role="status"
                        size="sm"
                        variant="light"
                    />
                )}
                <span className="me-2">{newStatus?.label}</span>
            </Dropdown.Toggle>
            <Dropdown.Menu role="listbox">
                {reminderStatusOptions.map((option) => (
                    <Dropdown.Item
                        key={option.value}
                        className="option-with-description__option"
                        onClick={() => setNewStatus(option)}
                        role="option"
                    >
                        <div className="name">{option.label}</div>
                        <div className="description">{option.description}</div>
                    </Dropdown.Item>
                ))}
            </Dropdown.Menu>
        </Dropdown>
    );
}
